import { ChevronDown, Download, FileSpreadsheet, Printer } from "lucide-react";
import { useMemo, useState } from "react";
import { useGetList } from "ra-core";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { usePipelines } from "../pipelines/usePipelines";
import {
  centsColumn,
  exportCsv,
  exportXlsx,
  printReport,
} from "./reportExport";
import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ListPageHeader } from "../list/ListPageHeader";
import {
  presetRange,
  toDateInput,
  useReport,
  type PresetRange,
  type ReportFilters,
  type ReportName,
} from "./useReport";
import { ExecutivePanel } from "./panels/ExecutivePanel";
import { ForecastPanel } from "./panels/ForecastPanel";
import { FinancePanel } from "./panels/FinancePanel";
import { GeographyPanel } from "./panels/GeographyPanel";
import { MarketingPanel } from "./panels/MarketingPanel";
import { SalesPanel } from "./panels/SalesPanel";
import { TasksPanel } from "./panels/TasksPanel";
import {
  executiveTables,
  financeTables,
  marketingTables,
  salesTables,
  geographyTables,
  tasksTables,
  type ExecutiveReport,
  type ForecastReport,
  forecastTables,
  type GeographyReport,
  type FinanceReport,
  type MarketingReport,
  type SalesReport,
  type TasksReport,
} from "./reportTables";

const inputClass =
  "h-9 rounded-md border border-input bg-transparent px-2.5 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring";

const TABS: { key: ReportName; label: string }[] = [
  { key: "executive", label: "Executive" },
  { key: "sales", label: "Sales" },
  { key: "forecast", label: "Forecast" },
  { key: "marketing", label: "Marketing" },
  { key: "tasks", label: "Tasks" },
  { key: "finance", label: "Finance" },
  { key: "geography", label: "Geography" },
];

const PRESETS: { key: Exclude<PresetRange, "custom">; label: string }[] = [
  { key: "this_month", label: "This month" },
  { key: "last_30", label: "Last 30 days" },
  { key: "this_quarter", label: "This quarter" },
  { key: "this_year", label: "This year" },
];

interface Sales {
  id: number;
  first_name: string;
  last_name: string;
}

/**
 * The reporting hub: five role dashboards over one shared filter bar.
 *
 * Every number is computed by Postgres (`report_*` RPCs) rather than summed in
 * the browser, so the figures stay right as a workspace grows past a page of
 * rows. Owner and pipeline filters are passed to the RPC, not applied to the
 * result, for the same reason.
 */
export const ReportsPage = () => {
  const [tab, setTab] = useState<ReportName>("executive");
  const [preset, setPreset] = useState<PresetRange>("this_month");
  const [range, setRange] = useState(() => presetRange("this_month"));
  const [ownerId, setOwnerId] = useState<number | null>(null);
  const { pipelines } = usePipelines();
  const [pipelineId, setPipelineId] = useState<string | null>(null);

  const { data: team } = useGetList<Sales>("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_name", order: "ASC" },
  });

  const filters: ReportFilters = useMemo(
    () => ({
      from: range.from,
      to: range.to,
      ownerId,
      pipelineId: tab === "sales" ? pipelineId : null,
    }),
    [range, ownerId, pipelineId, tab],
  );

  const applyPreset = (next: PresetRange) => {
    setPreset(next);
    if (next !== "custom") setRange(presetRange(next));
  };

  const setBound = (bound: "from" | "to", value: string) => {
    setPreset("custom");
    setRange((current) => ({ ...current, [bound]: value }));
  };

  return (
    <div className="min-w-0">
      <ListPageHeader
        title="Reports"
        subtitle="Executive, sales, marketing, task, finance and geographic views of real workspace data"
      />

      <div
        role="tablist"
        aria-label="Report"
        // Scrolls sideways on a phone rather than wrapping to two rows. No
        // negative margin here: at the page level it bleeds 4px past the
        // viewport and makes the whole page scroll horizontally on a phone.
        className="mb-3 flex min-w-0 gap-1 overflow-x-auto pb-1 print:hidden"
      >
        {TABS.map((item) => (
          <button
            key={item.key}
            role="tab"
            aria-selected={tab === item.key}
            onClick={() => setTab(item.key)}
            className={cn(
              "h-9 shrink-0 rounded-md px-3 text-[13px] font-medium transition-colors",
              tab === item.key
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-accent hover:text-foreground",
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2 print:hidden">
        <div className="flex flex-wrap items-center gap-1 rounded-lg border border-border bg-card p-0.5">
          {PRESETS.map((item) => (
            <button
              key={item.key}
              onClick={() => applyPreset(item.key)}
              className={cn(
                "h-7 rounded-md px-2.5 text-[12.5px] font-medium transition-colors",
                preset === item.key
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground",
              )}
            >
              {item.label}
            </button>
          ))}
        </div>

        <input
          type="date"
          aria-label="From date"
          className={inputClass}
          value={range.from}
          max={range.to}
          onChange={(event) => setBound("from", event.target.value)}
        />
        <input
          type="date"
          aria-label="To date"
          className={inputClass}
          value={range.to}
          min={range.from}
          max={toDateInput(new Date())}
          onChange={(event) => setBound("to", event.target.value)}
        />

        {tab === "sales" || tab === "tasks" ? (
          <select
            aria-label="Owner"
            className={inputClass}
            value={ownerId ?? ""}
            onChange={(event) =>
              setOwnerId(event.target.value ? Number(event.target.value) : null)
            }
          >
            <option value="">Everyone</option>
            {(team ?? []).map((member) => (
              <option key={member.id} value={member.id}>
                {member.first_name} {member.last_name}
              </option>
            ))}
          </select>
        ) : null}

        {tab === "sales" && pipelines.length > 1 ? (
          <select
            aria-label="Pipeline"
            className={inputClass}
            value={pipelineId ?? ""}
            onChange={(event) => setPipelineId(event.target.value || null)}
          >
            <option value="">All pipelines</option>
            {pipelines.map((pipeline) => (
              <option key={pipeline.id} value={pipeline.id}>
                {pipeline.name}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      {/* Printed output loses the tab strip and filter bar, so it needs to
          say for itself which report this is and over what period. */}
      <div className="mb-4 hidden print:block">
        <h2 className="text-[16px] font-semibold text-foreground">
          {TABS.find((item) => item.key === tab)?.label} report
        </h2>
        <p className="text-[12px] text-muted-foreground">
          {range.from} to {range.to}
        </p>
      </div>

      <ReportBody tab={tab} filters={filters} />
    </div>
  );
};

const ReportBody = ({
  tab,
  filters,
}: {
  tab: ReportName;
  filters: ReportFilters;
}) => {
  const { data, isPending, error } = useReport<unknown>(tab, filters);

  const filename = `gravun-${tab}-${filters.from}-to-${filters.to}`;

  if (isPending) {
    return (
      <p className="py-12 text-center text-[13px] text-muted-foreground">
        Loading report…
      </p>
    );
  }

  if (error) {
    return (
      <p className="py-12 text-center text-[13px] text-destructive">
        {error instanceof Error
          ? error.message
          : "The report could not be loaded."}
      </p>
    );
  }

  const rendered = (() => {
    switch (tab) {
      case "executive": {
        const report = data as ExecutiveReport;
        return {
          node: <ExecutivePanel data={report} />,
          tables: executiveTables(report),
        };
      }
      case "sales": {
        const report = data as SalesReport;
        return {
          node: <SalesPanel data={report} />,
          tables: salesTables(report),
        };
      }
      case "forecast": {
        const report = data as ForecastReport;
        return {
          node: <ForecastPanel data={report} />,
          tables: forecastTables(report),
        };
      }
      case "marketing": {
        const report = data as MarketingReport;
        return {
          node: <MarketingPanel data={report} />,
          tables: marketingTables(report),
        };
      }
      case "tasks": {
        const report = data as TasksReport;
        return {
          node: <TasksPanel data={report} />,
          tables: tasksTables(report),
        };
      }
      case "finance": {
        const report = data as FinanceReport;
        return {
          node: <FinancePanel data={report} />,
          tables: financeTables(report),
        };
      }
      case "geography": {
        const report = data as GeographyReport;
        return {
          node: <GeographyPanel data={report} />,
          tables: geographyTables(report),
        };
      }
    }
  })();

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end print:hidden">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-9 gap-1.5">
              <Download className="size-3.5" />
              Export
              <ChevronDown className="size-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={() => exportCsv(rendered.tables, filename)}
            >
              <Download className="mr-2 size-3.5" />
              CSV
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={() => exportXlsx(rendered.tables, filename)}
            >
              <FileSpreadsheet className="mr-2 size-3.5" />
              Excel (.xlsx)
            </DropdownMenuItem>
            <DropdownMenuItem className="cursor-pointer" onClick={printReport}>
              <Printer className="mr-2 size-3.5" />
              PDF / print
            </DropdownMenuItem>
            {tab === "finance" ? (
              // A bookkeeper wants a flat ledger they can pivot, not the
              // summarised finance panel, so it is its own export.
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={async () => {
                  const { data, error } = await getSupabaseClient().rpc(
                    "report_accounting_export",
                    {
                      p_workspace_id: getActiveWorkspaceId(),
                      p_from: filters.from,
                      p_to: filters.to,
                    },
                  );
                  if (error || !data) return;
                  const rows = data as Record<string, unknown>[];
                  exportCsv(
                    [
                      {
                        name: "Accounting ledger",
                        columns: [
                          "Date",
                          "Type",
                          "Reference",
                          "Customer",
                          "Description",
                          "Method",
                          "Status",
                          "Subtotal",
                          "Tax",
                          "Amount",
                        ],
                        rows: rows.map((r) => [
                          String(r.entry_date ?? ""),
                          String(r.entry_type ?? ""),
                          String(r.reference ?? ""),
                          String(r.customer ?? ""),
                          String(r.description ?? ""),
                          String(r.method ?? ""),
                          String(r.status ?? ""),
                          centsColumn(r.subtotal_cents as number | null),
                          centsColumn(r.tax_cents as number | null),
                          centsColumn(r.amount_cents as number | null),
                        ]),
                      },
                    ],
                    `gravun-accounting-${filters.from}-to-${filters.to}`,
                  );
                }}
              >
                <FileSpreadsheet className="mr-2 size-3.5" />
                Accounting ledger (CSV)
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {rendered.node}
    </div>
  );
};
